import type { FC, SVGProps } from 'react';
import { Link } from '@upright/ui/common/generic/components/link';
import { Text } from '@upright/ui/common/generic/components/typography';

import styles from './EntityCard.module.css';

export type Props = {
  /** Where the card goes. A real destination, because the whole card is an anchor. */
  href: string;
  /**
   * What the reader is looking for: a company, a fund, a portfolio. Truncates to one line rather
   * than wrapping, so a row of cards keeps its kinds on a shared baseline.
   */
  name: string;
  /**
   * What kind of thing it is, in the product's own words ("Company", "Fund", "Portfolio"). Sits
   * *above* the name, not below it: a reader scanning a mixed list sorts by kind first and then
   * reads the name, and a label under the name it qualifies arrives too late to do that.
   */
  kind: string;
  /** The kind's icon for the lilac chip, rendered at 20px inside the 40px chip. */
  icon: FC<SVGProps<SVGSVGElement>>;
};

/**
 * A whole-card link to a thing in the product. `design/50-components.md` "Card" is the spec this
 * implements, and the investor launcher's Favorites section is its shipped exemplar.
 *
 * **The navigating variant, and the point of it is what it does not have.** It takes the action
 * card's radius, inset, chip and hover pair, so the two read as one family and a favorites grid
 * under a prompts grid keeps one icon column down the page. It does **not** take the trailing
 * arrow or the shadow lift, because `design/50-components.md` reads both as promises that
 * something *starts*, and this goes somewhere instead. Do not add either back: an entity card
 * wearing an arrow is the symptom that section names.
 *
 * **It is an anchor, and that is a feature rather than an implementation detail.** Middle-click,
 * cmd-click, open-in-new-tab and the browser's own hover preview of the destination all work,
 * none of which a `<button>` launcher can offer. `ActionCard` takes `onClick` and no `href` for
 * the mirror-image reason.
 *
 * **Kind above name, and never in caps.** Uppercase is the sidebar section treatment and nothing
 * else (`design/10-foundations.md` "Typography"), so the kind is separated by weight and colour
 * the way every other muted label in the system is.
 */
export function EntityCard(props: Props) {
  const { href, name, kind, icon: Icon } = props;

  return (
    <div className={styles.wrapper}>
      <Link href={href} decoration="none">
        <span className={styles.root}>
          <span className={styles.chip}>
            <Icon width={20} height={20} />
          </span>
          {/*
           * Both lines set their leading, because the page inherits `body { line-height: 1.5 }`
           * unitless: a size without its paired `height` re-multiplies 1.5 by its own font size
           * and reserves 21px for a 14px line where the pairing asks for 20px.
           */}
          <span className={styles.text}>
            <Text size="sm" height="xs" color="gray-600">
              {kind}
            </Text>
            <Text size="md" height="sm" weight="bold" color="gray-900" truncate>
              {name}
            </Text>
          </span>
        </span>
      </Link>
    </div>
  );
}
